import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tooltip,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import AdminHeader from "components/Headers/AdminHeader";
import DetailsHeader from "components/CommonComponents/DetailsHeader";
import { useNavigate, useParams } from "react-router-dom";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import { ThumbnailPreview, mapBackendTemplateToFrontend, downloadImage, generateCertificateFile } from "components/CommonComponents/CertificatePreview";
import CustomPagination from "components/CommonComponents/ReactTable/CustomPagination";
import { useDispatch, useSelector } from "react-redux";
import { getUserCertificatesByBatch } from "action/issuance";
import { saveTraineeCertificateImage } from "action/certificate";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";

const IssuanceCertificatesList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { notify } = useContext(NotificationContext);

  const decodedId = id ? urlDecodedId(id) : null;

  const [activeTab, setActiveTab] = useState("completion");
  
  const [pageNums, setPageNums] = useState({
    completion: 1,
    participation: 1,
  });
  
  const pageNum = pageNums[activeTab];
  const setPageNum = (num) => setPageNums((prev) => ({ ...prev, [activeTab]: num }));

  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  
  const [filterStates, setFilterStates] = useState({
    completion: { searchName: "", searchEmail: "", searchMobile: "", filterParams: [] },
    participation: { searchName: "", searchEmail: "", searchMobile: "", filterParams: [] },
  });
  const filterState = filterStates[activeTab];
  const setFilterState = (updater) => setFilterStates((prev) => {
    const newState = typeof updater === "function" ? updater(prev[activeTab]) : updater;
    return { ...prev, [activeTab]: newState };
  });

  const [listStates, setListStates] = useState({
    completion: { isLoading: true, isSearched: false, error: "", noRecords: false, showLoaderOnClick: false },
    participation: { isLoading: true, isSearched: false, error: "", noRecords: false, showLoaderOnClick: false },
  });
  const listState = listStates[activeTab];
  const setListState = (updater) => setListStates((prev) => {
    const newState = typeof updater === "function" ? updater(prev[activeTab]) : updater;
    return { ...prev, [activeTab]: newState };
  });

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setListStates(prev => ({
        ...prev,
        [tab]: { ...prev[tab], isLoading: true }
      }));
    }
  };

  const [tooltipOpen, setTooltipOpen] = useState({});
  const toggleTooltip = (key) => {
    setTooltipOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };


  const userCertificatesResponse = useSelector(
    (state) => state.issuance.userCertificatesBatch
  );

  useEffect(() => {
    return () => {
      dispatch({ type: "USER_CERTIFICATES_BATCH", payload: {} });
    };
  }, [dispatch]);

  const fetchCertificates = () => {
    if (decodedId) {
      let finalParams = [];
      if (typeof filterState.filterParams === "string") {
        try {
          finalParams = JSON.parse(filterState.filterParams);
        } catch (e) {}
      } else if (Array.isArray(filterState.filterParams)) {
        finalParams = [...filterState.filterParams];
      }
      
      finalParams.push({
        field: "CertificateType",
        condition: "like",
        filterValues: [activeTab === "completion" ? "COMPLETION" : "PARTICIPATION"],
      });

      getUserCertificatesByBatch(dispatch, decodedId, pageNum, JSON.stringify(finalParams));
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, [dispatch, decodedId, pageNum, filterState.filterParams, activeTab]);

  useEffect(() => {
    if (userCertificatesResponse?.code === 200) {
      setListState({
        isLoading: false,
        isSearched: false,
        error: "",
        noRecords: userCertificatesResponse?.data?.userCertifcates?.length === 0 || userCertificatesResponse?.data?.userCertificates?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (userCertificatesResponse?.code || userCertificatesResponse?.message) {
      setListState({
        isLoading: false,
        isSearched: false,
        error: userCertificatesResponse?.message || "Failed to load certificates",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [userCertificatesResponse]);

  const buildFilterParams = () => {
    const filterParams = [];

    if (filterState.searchName?.trim()) {
      filterParams.push({
        field: "TraineeName",
        condition: "like",
        filterValues: [filterState.searchName.trim()],
      });
    }

    if (filterState.searchEmail?.trim()) {
      filterParams.push({
        field: "Email",
        condition: "like",
        filterValues: [filterState.searchEmail.trim()],
      });
    }

    if (filterState.searchMobile?.trim()) {
      filterParams.push({
        field: "Mobile",
        condition: "like",
        filterValues: [filterState.searchMobile.trim()],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        isSearched: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const handleClear = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
      isSearched: false,
    }));
    setFilterState({
      searchName: "",
      searchEmail: "",
      searchMobile: "",
      filterParams: [],
    });
    
    setPageNum(1);
  };



  const data = userCertificatesResponse?.data || {};

  const batchDetails = {
    title: data.batch_name || "-",
    status: data.batch_status || "-",
    courseName: data.course_name || "-",
    batchStartDate: data.batch_start_date || "-",
    batchEndDate: data.batch_end_date || "-",
  };

  const handleViewClick = async (item, parsedTemplate) => {
    if (item.certificatePath) {
      setPreviewTemplate({ ...item, isImageUrl: true });
      setShowPreview(true);
    } else {
      setPreviewTemplate({ ...item, parsedTemplate });
      setShowPreview(true);

      if (parsedTemplate) {
        try {
          const file = await generateCertificateFile(
            parsedTemplate,
            `${item.certificateNumber || "Certificate"}.png`
          );
          if (file) {
            const formData = new FormData();
            formData.append("certificateImage", file);
            await saveTraineeCertificateImage(item.id, formData);
            
            fetchCertificates();
          }
        } catch (error) {
          notify("danger", "Failed to save generated certificate image: " + (error?.message || ""));
        }
      }
    }
  };

  const handlePreviewCancel = () => {
    setShowPreview(false);
    setPreviewTemplate(null);
  };
  const handleDownloadCertificate = async (item, parsedTemplate) => {
    setIsDownloading(true);
    if (item.certificatePath) {
      await downloadImage(
        item.certificatePath,
        `${item.certificateNumber || "Certificate"}.jpg`
      );
      setIsDownloading(false);
      return;
    }

    if (!parsedTemplate) {
      setIsDownloading(false);
      return;
    }

    try {
      const file = await generateCertificateFile(
        parsedTemplate,
        `${item.certificateNumber || "Certificate"}.png`
      );
      if (file) {
        const formData = new FormData();
        formData.append("certificateImage", file);
        
        await saveTraineeCertificateImage(item.id, formData);

        fetchCertificates();
        
        const fileUrl = window.URL.createObjectURL(file);
        await await downloadImage(fileUrl, `${item.certificateNumber || "Certificate"}.png`);
        window.URL.revokeObjectURL(fileUrl);
      }
    } catch (error) {
      notify("danger", "Failed to download or save generated certificate image: " + (error?.message || ""));
    }
    setIsDownloading(false);
  };

  return (
    <>
      <AdminHeader />
      {isDownloading && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Downloading… Please wait.
            </div>
          </div>
        </div>
      )}
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <DetailsHeader
              isLoading={!userCertificatesResponse?.data}
              onBack={() => navigate(-1)}
              backTooltip="Back to Issuance List"
              title={batchDetails.title}
              status={batchDetails.status}
              showEdit={false}
              chips={[
                {
                  id: "courseChip",
                  icon: "fas fa-book",
                  value: batchDetails.courseName,
                  tooltip: `Course: ${batchDetails.courseName}`,
                },
                {
                  id: "dateChip",
                  icon: "fas fa-calendar",
                  value: `${batchDetails.batchStartDate} to ${batchDetails.batchEndDate}`,
                  tooltip: "Batch Dates",
                },
              ]}
            />
          </Col>
        </Row>

        <Row className="justify-content-center mt-4">
          <Col xl="12">
            <Card className="certificates-card mt-3">
              <div className="pb-0 course-sticky-header card-header">
                <Nav className="custom-tabs cursor">
                  <NavItem>
                    <NavLink
                      className={activeTab === "completion" ? "active" : ""}
                      onClick={() => toggleTab("completion")}
                    >
                      Completion Certificate
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "participation" ? "active" : ""}
                      onClick={() => toggleTab("participation")}
                    >
                      Participation Certificate
                    </NavLink>
                  </NavItem>
                </Nav>
              </div>
              <CardBody>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId={activeTab}>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by trainee name"
                      className="mr-2 mb-2 filter-input-large template-search-input"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSearch();
                        }
                      }}
                    />
                    <Input
                      type="text"
                      placeholder="Search by trainee email"
                      className="mr-2 mb-2 filter-input-large template-search-input"
                      value={filterState.searchEmail}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchEmail: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSearch();
                        }
                      }}
                    />

                    <Input
                      type="text"
                      placeholder="Search by trainee mobile"
                      className="mr-2 mb-2 filter-input-large template-search-input"
                      value={filterState.searchMobile}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchMobile: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSearch();
                        }
                      }}
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${listState.isSearched ? "search-btn" : ""}`}
                      disabled={listState.isSearched}
                    >
                      {listState.isSearched ? (
                        <>
                          <i className="fa fa-spinner fa-spin mr-1" />
                        </>
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {!listState.isLoading && (activeTab === "completion" ? userCertificatesResponse?.data?.totalCompletionRecords : userCertificatesResponse?.data?.totalParticipationRecords) > 0 && (
                    <span className="total-records">
                      {filterState.filterParams.length > 0 ? "Showing " : "Total Records : "}
                      {userCertificatesResponse?.data?.totalFilteredRecords || 0} {filterState.filterParams.length > 0 && `of ${activeTab === "completion" ? (userCertificatesResponse?.data?.totalCompletionRecords || 0) : (userCertificatesResponse?.data?.totalParticipationRecords || 0)} `}
                      {filterState.filterParams.length > 0 ? "Records" : ""}
                    </span>
                  )}
                </div>

                {listState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : listState.error ? (
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i className="fa-solid fa-circle-exclamation"></i>
                    </div>
                    <span>{listState.error}</span>
                  </div>
                ) : listState.noRecords ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info"></i>
                    </div>
                    <span>No issued certificates found.</span>
                  </div>
                ) : (
                  <>
                    <Row className="certificates-grid">
                      {(userCertificatesResponse?.data?.userCertifcates || userCertificatesResponse?.data?.userCertificates || []).map((item, index) => {
                        const templateItem = { ...item, template: item.certificateJSON };
                        const parsedTemplate = mapBackendTemplateToFrontend(templateItem);

                        return (
                          <Col
                            key={item.id || item.ID || index}
                            xl="4"
                            lg="6"
                            md="6"
                            className="mb-4"
                          >
                            <Card className="certificate-tile">
                              <div className="certificate-top-bar">
                                <div className="text-muted small font-weight-bold">
                                  {item.certificateNumber}
                                </div>

                                <div className="certificate-actions-templates">
                                  <Button
                                    size="sm"
                                    className="icon-btn"
                                    onClick={() => handleViewClick(item, parsedTemplate)}
                                  >
                                    <i className="fa-solid fa-eye" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="icon-btn"
                                    onClick={() => handleDownloadCertificate(item, parsedTemplate)}
                                  >
                                    <i className="fa-solid fa-download" />
                                  </Button>
                                </div>
                              </div>

                              <div className="certificate-image-wrapper">
                                {item.certificatePath ? (
                                  <img src={item.certificatePath} alt="Certificate" className="w-100 h-100 certificate-preview-img-contain" />
                                ) : (
                                  <ThumbnailPreview parsedTemplate={parsedTemplate} />
                                )}
                              </div>

                              <CardBody className="pb-0">
                                <h4 className="certificate-title">
                                  {item.traineeName}
                                </h4>

                                <div className="d-flex justify-content-between mt-3">
                                  <div className="text-truncate mr-2 certificate-list-name-trunc">
                                    <div className="text-muted small">Email</div>
                                    <div>
                                      <span
                                        id={`emailTooltip-${index}`}
                                        className="text-truncate xs-text d-inline-block w-100"
                                      >
                                        {item.traineeEmail}
                                      </span>
                                    </div>
                                    {item.traineeEmail && (
                                      <Tooltip
                                        className="qb-tooltip"
                                        placement="auto"
                                        delay={{ show: 150, hide: 0 }}
                                        target={`emailTooltip-${index}`}
                                        isOpen={tooltipOpen[`email-${index}`]}
                                        toggle={() => toggleTooltip(`email-${index}`)}
                                        autohide={false}
                                      >
                                        {item.traineeEmail}
                                      </Tooltip>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <div className="text-muted small">Mobile</div>
                                    <div className="xs-text">
                                      {item.traineeMobile}
                                    </div>
                                  </div>
                                </div>
                                <div className="d-flex justify-content-between mt-2">
                                  <div>
                                    <div className="text-muted small">Issued On</div>
                                    <div className="xs-text">
                                      {(() => {
                                        let dateStr = item.issuedDate || item.issueDate || item.createdAt;
                                        if (!dateStr && item.certificateJSON) {
                                          try {
                                            const cj = JSON.parse(item.certificateJSON);
                                            dateStr = cj.createdAt || cj.created_at;
                                          } catch(e) {}
                                        }
                                        return dateStr || "-";
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>

                    {Math.ceil((userCertificatesResponse?.data?.totalFilteredRecords || 0) / ((userCertificatesResponse?.data?.noOfRecordsPerPage || 12) > 0 ? (userCertificatesResponse?.data?.noOfRecordsPerPage || 12) : 1)) > 1 && (
                      <Row className="mt-4">
                        <Col>
                          <CustomPagination
                            totalPages={Math.ceil((userCertificatesResponse?.data?.totalFilteredRecords || 0) / ((userCertificatesResponse?.data?.noOfRecordsPerPage || 12) > 0 ? (userCertificatesResponse?.data?.noOfRecordsPerPage || 12) : 1))}
                            activePage={pageNum}
                            setPageNum={setPageNum}
                          />
                        </Col>
                      </Row>
                    )}
                  </>
                )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Preview Popup Modal */}
      <Modal
        isOpen={showPreview}
        toggle={handlePreviewCancel}
        size="xl"
        centered
        className="preview-modal"
      >
        <ModalHeader toggle={handlePreviewCancel}>Certificate Preview</ModalHeader>

        <ModalBody className="text-center p-3">
          {previewTemplate &&
            (() => {
              if (previewTemplate.isImageUrl) {
                return (
                  <div className="preview-modal-content">
                    <img src={previewTemplate.certificatePath} alt="Certificate Preview" className="certificate-preview-modal-img" />
                  </div>
                );
              }

              const templateJson = previewTemplate.parsedTemplate || mapBackendTemplateToFrontend(previewTemplate);

              return (
                <div className="preview-modal-content">
                  <ThumbnailPreview parsedTemplate={templateJson} />
                </div>
              );
            })()}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handlePreviewCancel}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default IssuanceCertificatesList;
